import { Form, useAsync, Input, InputNumber, DatePicker, Select } from '@maxtropy/components';
import { OptionItem } from '@/pages/EquipmentLedger/util';
import React, { useCallback, useState } from 'react';
import { apiV2AssetTemplateListPost } from '@maxtropy/device-customer-apis-v2';
import { FIELD_TYPE, SELECT_TYPE } from '@/pages/AssetInformationManagement/constant';
import { Row, Col } from 'antd';
import { PickerMode } from 'rc-picker/lib/interface';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import dayjs, { isDayjs } from 'dayjs';
import { isNil } from 'lodash-es';

export const useAssetTemplateOptions = () => {
  const [typeId, setTypeId] = useState<number | undefined>();
  const options = useAsync<OptionItem[]>(
    useCallback(async () => {
      const list = (await apiV2AssetTemplateListPost({ id: typeId }))?.list || [];
      return list.map(i => ({
        label: i.name!,
        value: i.id!,
      }));
    }, [typeId])
  );
  return {
    options: options || [],
    setTypeId,
  };
};

const getDatePickerFormat = (format: string) => {
  switch (format) {
    case 'yyyy-MM':
      return {
        picker: 'month',
        format: 'YYYY-MM',
      };
    case 'yyyy':
      return {
        picker: 'year',
        format: 'YYYY',
      };
  }
  return {
    picker: 'date',
    format: 'YYYY-MM-DD',
  };
};

const getFormItemContainer = ({ name, fieldId, required, rules = [], children }: any) => {
  return (
    <Col span={8} key={Math.random()}>
      <Form.Item
        label={name}
        name={['assetTemplate', fieldId]}
        rules={required ? [{ required: true, message: '请输入' }, ...rules] : []}
      >
        {children}
      </Form.Item>
    </Col>
  );
};

export const getAssetTemplateRender = (list: any[], form: FormInstance<any>) => {
  if (!list || list.length === 0) return [];
  let node = <></>;

  return list.map((item: any) => {
    const { type, name, fieldId, required, unique, selectType, enumNameList, timeFormat, decimalDigits } = item;
    switch (type) {
      case FIELD_TYPE.TEXT:
        const rules = [];
        if (unique) {
          rules.push({
            validator: (_: any, value: any) => {
              // 除去自身的所有text
              const allTextFieldIds = list.filter((i: any) => i.type === FIELD_TYPE.TEXT).map((i: any) => i.fieldId);
              // 当前input爆红->修改另一个inputd的值->恢复
              // todo fix会无限调用,栈溢出
              // form.validateFields(allTextFieldIds.map((id: any) => ['assetTemplate', id]));
              const excludeSelfTextFieldIds = allTextFieldIds.filter((id: any) => id !== fieldId);
              const assetTemplate = form.getFieldValue('assetTemplate');
              const has = excludeSelfTextFieldIds.some((id: any) => assetTemplate[id] === value);
              if (value && has) {
                return Promise.reject('该输入框值唯一!');
              }
              return Promise.resolve();
            },
          });
        }
        node = getFormItemContainer({ name, fieldId, required, rules, children: <Input style={{ width: '100%' }} /> });
        break;
      case FIELD_TYPE.NUMBER:
        node = getFormItemContainer({
          name,
          fieldId,
          required,
          children: <InputNumber style={{ width: '100%' }} precision={decimalDigits} />,
        });
        break;
      case FIELD_TYPE.DATETIME:
        const pickerAndFormat = getDatePickerFormat(timeFormat);
        node = getFormItemContainer({
          name,
          fieldId,
          required,
          children: (
            <DatePicker
              style={{ width: '100%' }}
              format={pickerAndFormat.format}
              picker={pickerAndFormat.picker as PickerMode}
              placeholder="请选择"
            />
          ),
        });
        break;
      case FIELD_TYPE.ENUM:
        const options = enumNameList?.map((i: any) => ({ label: i, value: i })) || [];
        const mode = selectType === SELECT_TYPE.MULTIPLE ? 'multiple' : undefined;
        const children = <Select style={{ width: '100%' }} options={options} mode={mode} />;
        node = getFormItemContainer({
          name,
          fieldId,
          required,
          children,
        });
        break;
    }
    return node;
  });
};

// 提取前端数据->后端需要的格式
export const fromTemplateDataToRequestPayload = (values: any, fieldDataList: any[], templateId: number | string) => {
  const assetTemplate = { ...values.assetTemplate };
  for (let i = 0; i < fieldDataList.length; i++) {
    const item = fieldDataList[i];
    const fieldId = item.fieldId;
    const value = assetTemplate[fieldId];
    if (isNil(value)) {
      item.fieldValue = [];
    } else if (typeof value === 'number') {
      const decimalDigits = item.decimalDigits ?? 2;
      item.fieldValue = [value.toFixed(decimalDigits)];
    } else if (isDayjs(value)) {
      const { format } = getDatePickerFormat(item.timeFormat);
      item.fieldValue = [value.format(format)];
    } else if (Array.isArray(value)) {
      item.fieldValue = value;
    } else {
      item.fieldValue = [value];
    }
    delete assetTemplate[fieldId];
  }
  assetTemplate['fieldDataList'] = fieldDataList;
  assetTemplate['templateName'] = fieldDataList.find(i => i.value === templateId)?.label;
  return assetTemplate;
};

// 接口数据->前端需要的
export const fromResponseToTemplateData = (res: any) => {
  const assetTemplate = res?.assetTemplate ?? {};
  const fieldDataList = assetTemplate.fieldDataList ?? [];
  fieldDataList.forEach((item: any) => {
    if (item.type === FIELD_TYPE.ENUM) {
      assetTemplate[item.fieldId] = item.fieldValue;
    } else if (item.type === FIELD_TYPE.DATETIME) {
      assetTemplate[item.fieldId] = item.fieldValue?.[0] ? dayjs(item.fieldValue[0]) : undefined;
    } else {
      assetTemplate[item.fieldId] = item.fieldValue?.[0];
    }
  });
  delete assetTemplate.fieldDataList;
  return {
    assetTemplate,
    fieldDataList,
  };
};

// 详情展示: 接口数据->label值
export const fromResponseToTemplateLabel = (res: any) => {
  const assetTemplate = res?.assetTemplate ?? {};
  const fieldDataList = assetTemplate.fieldDataList ?? [];
  fieldDataList.forEach((item: any) => {
    if (item.type === FIELD_TYPE.ENUM) {
      assetTemplate[item.fieldId] = item.fieldValue?.join('，') || '--';
    } else if (item.type === FIELD_TYPE.DATETIME) {
      const value = item.fieldValue?.[0];
      assetTemplate[item.fieldId] = value ? value : '--';
    } else {
      assetTemplate[item.fieldId] = item.fieldValue?.[0] ?? '--';
    }
  });
  return assetTemplate;
};
